import { useState, useCallback } from 'react';
import { autoFixApi } from '../api/autoFix';
import type { AutoFixRun } from '../api/autoFix';

export function useAutoFix() {
  const [runs, setRuns] = useState<AutoFixRun[]>([]);
  const [activeRun, setActiveRun] = useState<AutoFixRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchRuns = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await autoFixApi.list(p);
      setRuns(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch {
      setError('Failed to load fix runs.');
    } finally {
      setLoading(false);
    }
  }, []);

  const createPlan = useCallback(async () => {
    setPlanning(true);
    setError(null);
    try {
      const res = await autoFixApi.plan();
      const run = res.data.data;
      // Fetch full run with actions
      const detail = await autoFixApi.show(run.id);
      setActiveRun(detail.data.data);
      return detail.data.data;
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to create fix plan.';
      setError(msg);
      return null;
    } finally {
      setPlanning(false);
    }
  }, []);

  const applyRun = useCallback(async (id: number) => {
    setApplying(true);
    setError(null);
    try {
      const res = await autoFixApi.apply(id);
      const updated = res.data.data;
      setActiveRun((prev) => (prev?.id === id ? updated : prev));
      setRuns((prev) => prev.map((r) => (r.id === id ? updated : r)));
      return updated;
    } catch {
      setError('Failed to apply fixes. Some actions may have succeeded.');
      return null;
    } finally {
      setApplying(false);
    }
  }, []);

  const deleteRun = useCallback(async (id: number) => {
    try {
      await autoFixApi.destroy(id);
      setRuns((prev) => prev.filter((r) => r.id !== id));
      if (activeRun?.id === id) setActiveRun(null);
    } catch {
      setError('Failed to delete fix run.');
    }
  }, [activeRun]);

  return {
    runs, activeRun, loading, planning, applying, error,
    total, page, lastPage,
    fetchRuns, createPlan, applyRun, deleteRun,
    setActiveRun, setError,
  };
}
